import { supabase } from './supabase';
import { edgeErrorMessage } from './canvasPipeline';

export type CanvasAskField = {
  id: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  recommended?: string;
};

/** `note` = why the agent recommends the pre-selected options. */
export type CanvasAsk = { prompt?: string; fields: CanvasAskField[]; note?: string };

function keyframeChoiceValue(ask: CanvasAsk | undefined, answers: Record<string, string> | undefined): string {
  const kf = ask?.fields?.find((f) => f.id === 'keyframes');
  return String(answers?.keyframes || kf?.recommended || '').trim().toLowerCase();
}

const DECLINED_KEYFRAMES = new Set(['nao', 'não', 'no', 'false']);

export function recommendedAnswers(ask?: CanvasAsk): Record<string, string> {
  const out: Record<string, string> = {};
  const declining = DECLINED_KEYFRAMES.has(keyframeChoiceValue(ask, undefined));
  for (const f of ask?.fields || []) {
    // A "no keyframes" advice must not pre-select a scene total.
    if (declining && f.id === 'scene_count') continue;
    if (f.recommended && f.options.some((o) => o.value === f.recommended)) out[f.id] = f.recommended;
  }
  return out;
}

/** Shown instead of the keyframe question when the advice is to use the photos as they are. */
export const DIRECT_VIDEO_NOTE = 'Vou criar o vídeo com as imagens da tela.';

/**
 * The agent already decided not to generate intermediate frames.
 * The question stays only if the person switched the answer to "sim".
 */
export function keyframeAdviceDeclines(
  ask?: CanvasAsk,
  answers?: Record<string, string>,
): boolean {
  const advised = keyframeChoiceValue(ask, undefined);
  if (!DECLINED_KEYFRAMES.has(advised)) return false;
  const choice = String(answers?.keyframes || advised).trim().toLowerCase();
  return DECLINED_KEYFRAMES.has(choice);
}

/**
 * Scene count is the follow-up to "yes, create keyframes".
 * While the answer (or the advice) is not to generate, that block stays out of the form.
 * If the advice was "no" and the person switches to "yes", the counts appear without a
 * recommended badge — the agent never recommended a generated total.
 */
export function askFieldsForDisplay(
  ask: CanvasAsk | undefined,
  answers?: Record<string, string>,
): CanvasAskField[] {
  const fields = ask?.fields || [];
  const choice = keyframeChoiceValue(ask, answers);
  if (DECLINED_KEYFRAMES.has(choice)) return fields.filter((f) => f.id !== 'scene_count');
  const advised = String(ask?.fields?.find((f) => f.id === 'keyframes')?.recommended || '').trim().toLowerCase();
  if (DECLINED_KEYFRAMES.has(advised)) {
    return fields.map((f) => (f.id === 'scene_count' ? { ...f, recommended: undefined } : f));
  }
  return fields;
}

/** Drops the "how many scenes" clause while that field is hidden. Other replies stay as written. */
export function askPromptForDisplay(text: string | undefined, fields: CanvasAskField[]): string | undefined {
  if (!text || fields.some((f) => f.id === 'scene_count')) return text;
  const next = text
    .replace(/\s*se sim,?\s+com quantas cenas(?:\s+no total)?\s*\??/i, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
  return next || text;
}

/** Answers the user can confirm: hidden fields are left out, so "não" does not send a scene total. */
export function resolveAskSubmission(
  ask: CanvasAsk | undefined,
  answers: Record<string, string> | undefined,
): { fields: CanvasAskField[]; answers: Record<string, string> } {
  const fields = askFieldsForDisplay(ask, answers);
  const resolved: Record<string, string> = {};
  for (const f of fields) {
    const picked = answers?.[f.id];
    if (picked && f.options.some((o) => o.value === picked)) resolved[f.id] = picked;
    else if (f.options[0]) resolved[f.id] = f.options[0].value;
  }
  return { fields, answers: resolved };
}

export type CanvasIntent =
  | {
      type: 'build_storyboard';
      image_node_ids: string[];
      brief: string;
      keyframes: boolean;
      scene_count: number;
      duration_seconds?: number;
      new_scenes_position?: 'between' | 'before' | 'after';
      /** The user started over: forget this video's earlier requests. */
      reset_requests?: boolean;
    }
  | { type: 'generate_keyframes'; video_node_id?: string }
  | { type: 'generate_video'; video_node_id?: string }
  | { type: 'normalize_images'; node_ids: string[]; aspect_ratio?: string; match_resolution?: boolean }
  /** `instruction` is the user's ask in pt-BR (shown in the modal); `prompt_en` is the same edit for the image model. */
  | {
      type: 'edit_images';
      node_ids: string[];
      instruction: string;
      prompt_en?: string;
      /** Extra stills passed to the edit model (e.g. room photo as background plate). */
      reference_node_ids?: string[];
      /** After edits, open debit to regenerate this flow video (new version on the same node). */
      regenerate_video_id?: string;
    }
  /**
   * Place `background_node_id` behind a solid-color subject.
   * Prefer `video_node_id` when a clip is already ready (local matte or V2V).
   * `node_ids` = stills only when there is no ready clip.
   */
  | {
      type: 'apply_background';
      background_node_id: string;
      node_ids?: string[];
      video_node_id?: string;
      regenerate_video_id?: string;
    }
  /**
   * New still from existing stills. Sources are not edited.
   * The client draws a reference arrow from each source to the new node; that arrow does not feed video.
   */
  | {
      type: 'compose_image';
      source_node_ids: string[];
      instruction: string;
      prompt_en?: string;
    }
  | { type: 'connect'; from: string; to: string; role?: 'flow' | 'ref' }
  | { type: 'disconnect'; from: string; to: string }
  | { type: 'join_videos'; video_node_ids: string[] }
  /** `original` = board takes the shape of its photos. */
  | { type: 'set_aspect_ratio'; aspect_ratio: '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | 'original' }
  | { type: 'set_image_mode'; video_node_id?: string; image_mode: 'keyframes' | 'reference' }
  | { type: 'delete_nodes'; node_ids: string[] }
  | {
      type: 'add_audio';
      audio_kind: 'voice' | 'music' | 'sfx';
      /** Voice: the narration script. Music / effect: the style or sound description. */
      text: string;
      video_node_id?: string;
      title?: string;
      start_offset?: number;
      loop?: boolean;
      volume?: number;
      /** Music made on top of this voice node (Add Instrumental). */
      over_voice_id?: string;
    }
  | { type: 'regenerate_audio'; audio_node_id: string; text?: string }
  | {
      type: 'update_audio';
      audio_node_id: string;
      volume?: number;
      start_offset?: number;
      loop?: boolean;
      muted?: boolean;
      speed?: number;
    }
  | {
      type: 'edit_video';
      video_node_id?: string;
      source_volume?: number;
      captions?: boolean;
      caption_position?: 'top' | 'center' | 'bottom';
      fade?: boolean;
      grade?: boolean;
      /** Desaturate the edited video (true black-and-white). */
      grayscale?: boolean;
    }
  | {
      type: 'join_transition';
      video_node_id?: string;
      transition: 'none' | 'fade' | 'crossfade';
      seconds?: number;
    }
  /** The client sends the flow's images to `reorderCanvasImages`, which returns the rebuilt storyboard. */
  | { type: 'reorder_images'; video_node_id?: string; brief: string }
  /** Repositions nodes on the canvas. Does not change edges or the storyboard. */
  | { type: 'layout_board' }
  /** Revise the Explore template prompt that rendered this video, then render again. The prompt stays on the server. */
  | { type: 'revise_template'; video_node_id: string; change: string };

export type CanvasImageCaption = { node_id: string; text: string };

export type CanvasAgentResponse = {
  text: string;
  ask?: CanvasAsk;
  /** Follow-up requests shown as one-click chips under the reply. */
  suggestions?: string[];
  /** An Explore example the reply can open. The catalog search stays off the model prompt. */
  exploreOffer?: CanvasExploreOffer;
  intents: CanvasIntent[];
  /** What each newly described image shows; kept on the node for later turns. */
  captions?: CanvasImageCaption[];
  transcript?: string;
  credits_charged: number;
  input_tokens: number;
  output_tokens: number;
};

/** A Referências card does not ask for extra stills. Drop that question. */
export function dropKeyframeAsk(ask?: CanvasAsk): CanvasAsk | undefined {
  if (!ask?.fields?.length) return ask;
  const blob = [
    ask.prompt,
    ...ask.fields.flatMap((f) => [f.id, f.label, ...f.options.map((o) => `${o.value} ${o.label}`)]),
  ].join(' ');
  if (/keyframe|quadro[- ]chave|mais refer[eê]ncias|gerar mais refer/i.test(blob)) return undefined;
  return ask;
}

/** Kept for threads saved before Referências stopped offering extra stills. The chat does not render it. */
export type CanvasModeAsk = {
  videoIds: string[];
  storyboard?: Extract<CanvasIntent, { type: 'build_storyboard' }>;
  userTexts?: string[];
  answered?: 'yes' | 'no';
};

/** A debit confirmation the chat can reopen. `done` once the user confirms. */
export type CanvasDebit = {
  label: string;
  done?: boolean;
} & (
  | { kind: 'image'; videoIds: string[] }
  | { kind: 'video'; videoIds: string[] }
  | { kind: 'revise-template'; videoId: string; change: string }
  | { kind: 'audio'; audioId: string }
  | { kind: 'gen-image'; at: { x: number; y: number }; prompt: string }
  | {
      kind: 'edit-image';
      nodeIds: string[];
      instruction: string;
      promptEn?: string;
      referenceNodeIds?: string[];
      regenerateVideoId?: string;
    }
  | {
      kind: 'compose-image';
      nodeId: string;
      sourceNodeIds: string[];
      instruction: string;
      promptEn?: string;
    }
  | {
      kind: 'outpaint';
      jobs: Array<{ nodeId: string; frame: { width: number; height: number }; outpaint: boolean }>;
      aspect?: string;
      summary: string;
      preferRecompose?: boolean;
    }
);

/** Buttons under an assistant reply: open the Explore modal, or continue the normal script. */
export type CanvasExploreOffer = {
  templateId: string;
  title: string;
  texts?: Record<string, string>;
  /** Clip length the agent picked from the photos and the request. */
  durationSeconds?: number;
  /** Photos on the board when the agent answered, in reading order (or the start/end picks). */
  imageUrls?: string[];
  /** Board node ids chosen for the template image slots (start/end), when fewer than all photos. */
  imageNodeIds?: string[];
  /** Explains that extra board photos will not go into the template. */
  unusedImagesNote?: string;
  resolved?: 'opened' | 'declined';
};

export type CanvasChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Labels of the nodes selected (referenced) when the user sent this message. */
  refs?: string[];
  audioSeconds?: number;
  ask?: CanvasAsk;
  askAnswers?: Record<string, string>;
  askResolved?: boolean;
  suggestions?: string[];
  exploreOffer?: CanvasExploreOffer;
  credits?: number;
  inputTokens?: number;
  outputTokens?: number;
  generationCredits?: number;
  generationKind?: 'image' | 'video' | 'audio';
  status?: string;
  /** Script (or another long step) is still running; the bubble shows a spinner and hides follow-ups. */
  busy?: boolean;
  /** Debit confirmation this reply opened. `done` after the user confirms; the button stays to reopen until then. */
  debit?: CanvasDebit;
  /** Referências → Contínuo, waiting on Sim / Não under this reply. */
  modeAsk?: CanvasModeAsk;
  error?: boolean;
  /** Divider: the agent forgets everything before it (board was cleared). */
  contextBreak?: boolean;
  /** User message that only summarizes a form answer (not a request for the video). */
  formAnswer?: boolean;
};

/** The compose prompt writer. Improves the user's intent unless they asked to leave the prompt alone. */
export async function writeComposeImagePrompt(body: {
  organization_id: string;
  message: string;
  image_thumbs?: Array<{ node_id: string; data: string }>;
}): Promise<{ prompt_en: string; credits_charged?: number; input_tokens?: number; output_tokens?: number }> {
  const { data, error } = await supabase.functions.invoke('compose-prompt', { body });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  const prompt = String(data?.prompt_en || '').trim();
  if (prompt.length < 8) throw new Error('Prompt vazio');
  return {
    prompt_en: prompt.slice(0, 2000),
    ...(Number(data?.credits_charged) > 0 ? { credits_charged: Number(data.credits_charged) } : {}),
    ...(Number(data?.input_tokens) > 0 ? { input_tokens: Number(data.input_tokens) } : {}),
    ...(Number(data?.output_tokens) > 0 ? { output_tokens: Number(data.output_tokens) } : {}),
  };
}

export async function sendCanvasAgentTurn(body: {
  organization_id: string;
  message?: string;
  audio?: { mime_type: string; data: string };
  history: Array<{ role: 'user' | 'assistant'; text: string }>;
  board: unknown;
  answers?: Record<string, string>;
  /** Downscaled JPEG base64 of the images a storyboard would use; only read when asking about keyframes. */
  image_thumbs?: Array<{ node_id: string; data: string }>;
  /** Images without a description yet; described once and returned in `captions`. */
  caption_thumbs?: Array<{ node_id: string; data: string }>;
}): Promise<CanvasAgentResponse> {
  const { data, error } = await supabase.functions.invoke('canvas-agent', { body });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  return parseAgentResponse(data);
}

/** Second step of `reorder_images`: the agent looks at the flow's images and returns the reordered storyboard. */
export async function reorderCanvasImages(body: {
  organization_id: string;
  reorder: { brief: string; image_node_ids: string[] };
  history: Array<{ role: 'user' | 'assistant'; text: string }>;
  board: unknown;
  image_thumbs?: Array<{ node_id: string; data: string }>;
}): Promise<CanvasAgentResponse> {
  const { data, error } = await supabase.functions.invoke('canvas-agent', { body });
  if (error || data?.error) throw new Error(await edgeErrorMessage(error, data));
  return parseAgentResponse(data);
}

function parseExploreOffer(raw: unknown): CanvasExploreOffer | undefined {
  const row = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
  const templateId = String(row?.template_id || '');
  if (!templateId) return undefined;
  const texts: Record<string, string> = {};
  const source = row?.texts && typeof row.texts === 'object' ? row.texts as Record<string, unknown> : {};
  for (const [key, value] of Object.entries(source)) {
    const text = String(value || '').trim();
    if (key && text) texts[key] = text.slice(0, 400);
  }
  const seconds = Math.round(Number(row?.duration_seconds));
  const imageNodeIds = Array.isArray(row?.image_node_ids)
    ? (row.image_node_ids as unknown[]).map((id) => String(id || '').trim()).filter(Boolean).slice(0, 12)
    : [];
  const unusedImagesNote = String(row?.unused_images_note || '').trim().slice(0, 280);
  return {
    templateId,
    title: String(row?.title || 'Modelo').slice(0, 80),
    ...(Object.keys(texts).length ? { texts } : {}),
    ...(seconds >= 1 && seconds <= 60 ? { durationSeconds: seconds } : {}),
    ...(imageNodeIds.length ? { imageNodeIds } : {}),
    ...(unusedImagesNote ? { unusedImagesNote } : {}),
  };
}

function parseAgentResponse(data: any): CanvasAgentResponse {
  return {
    text: String(data?.text || ''),
    ask: data?.ask?.fields?.length ? (data.ask as CanvasAsk) : undefined,
    suggestions: Array.isArray(data?.suggestions)
      ? (data.suggestions as unknown[]).map(String).filter(Boolean).slice(0, 3)
      : undefined,
    exploreOffer: parseExploreOffer(data?.explore_offer),
    intents: Array.isArray(data?.intents) ? (data.intents as CanvasIntent[]) : [],
    captions: Array.isArray(data?.captions)
      ? (data.captions as CanvasImageCaption[]).filter((c) => c?.node_id && c?.text)
      : undefined,
    transcript: data?.transcript ? String(data.transcript) : undefined,
    credits_charged: Number(data?.credits_charged) || 0,
    input_tokens: Number(data?.input_tokens) || 0,
    output_tokens: Number(data?.output_tokens) || 0,
  };
}
